import { useLocation } from '@reach/router';
import { graphql, useStaticQuery } from 'gatsby';
import React, { createContext, useContext, useMemo, useState } from 'react';

import { Category, determineCategory } from '../components/promo/utils/determineCategory';
import getPromoColors from '../components/promo/utils/getPromoColors';
import { TagItem } from '../components/tag/Tag';
import { SIGN_UP_LINK } from '../constants/const/siteLinks.const';

interface PromoContextType {
    promoCardIsVisible: boolean | undefined;
    setPromoCardIsVisible: (value: boolean | undefined) => void;
    data: Queries.PrismicPromotionsData;
    promoIsActive: boolean;
    category: Category;
    colors: { main: string; light: string };
    signupUrl: (category: Category) => string;
    tagList: TagItem[];
    setTagList: (tags: TagItem[]) => void;
}

interface PromoProviderProps {
    children: React.ReactNode;
}

const PromoContext = createContext<PromoContextType | undefined>(undefined);

const getSignupLink = (category: Category, data: Queries.PrismicPromotionsData, promoIsActive: boolean) => {
    if (!category) return '';
    let url = SIGN_UP_LINK[category].url;
    const promoCode = data[`code_promo_${category}`].text;

    if (promoIsActive && promoCode) {
        url += `&c=${promoCode}`;
    }

    return url;
};

const getPromoIsActive = (startDate: string, endDate: string): boolean => {
    const now = new Date();
    const start = new Date(startDate);
    const end = new Date(endDate);

    // Réinitialiser les heures pour comparer uniquement les dates
    const nowDateOnly = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startDateOnly = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    const endDateOnly = new Date(end.getFullYear(), end.getMonth(), end.getDate());

    // Définir le début de la journée pour la date de début (00:00:00.000)
    startDateOnly.setHours(0, 0, 0, 0);
    // Définir la fin de la journée pour la date de fin (23:59:59.999)
    endDateOnly.setHours(23, 59, 59, 999);

    return nowDateOnly >= startDateOnly && nowDateOnly <= endDateOnly;
};

export const PromoProvider: React.FC<PromoProviderProps> = ({ children }) => {
    const { prismicPromotions } = useStaticQuery(query);
    const [promoCardIsVisible, setPromoCardIsVisible] = useState<boolean | undefined>(undefined);
    const [tagList, setTagList] = useState<TagItem[]>([]);
    const location = useLocation();

    const value = useMemo(() => {
        const category = determineCategory(location.pathname + location.search, tagList);
        const promoIsActive = getPromoIsActive(prismicPromotions.data.start_date, prismicPromotions.data.end_date);

        return {
            promoCardIsVisible,
            setPromoCardIsVisible,
            data: prismicPromotions.data,
            promoIsActive,
            category,
            colors: getPromoColors(prismicPromotions.data.theme),
            signupUrl: (category: Category) => getSignupLink(category, prismicPromotions.data, promoIsActive),
            tagList,
            setTagList,
        };
    }, [promoCardIsVisible, prismicPromotions.data, location.pathname, location.search, tagList]);

    return <PromoContext.Provider value={value}>{children}</PromoContext.Provider>;
};

export const usePromoContext = (): PromoContextType => {
    const context = useContext(PromoContext);
    if (context === undefined) {
        throw new Error("usePromoContext doit être utilisé à l'intérieur d'un PromoProvider");
    }
    return context;
};

const query = graphql`
    {
        prismicPromotions {
            data {
                start_date
                end_date
                theme
                name {
                    text
                }
                icon {
                    text
                }
                banner_compta {
                    richText
                }
                banner_crea {
                    richText
                }
                banner_facturation {
                    richText
                }
                cover_compta {
                    richText
                }
                cover_crea {
                    richText
                }
                cover_facturation {
                    richText
                }
                chip_label {
                    text
                }
                new_price
                code_promo_compta {
                    text
                }
                code_promo_crea {
                    text
                }
                code_promo_facturation {
                    text
                }
                blog_banner_compta {
                    richText
                }
                blog_banner_crea {
                    richText
                }
                blog_banner_facturation {
                    richText
                }
                blog_cover_compta {
                    richText
                }
                blog_cover_crea {
                    richText
                }
                blog_cover_facturation {
                    richText
                }
            }
        }
    }
`;
